import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Zap, Plus, Edit2, Trash2, Globe, User as UserIcon, Search,
} from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

export default function QuickReplies() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("defaults");
  const [data, setData] = useState({ defaults: [], personal: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/quick-replies`, { withCredentials: true });
      setData(res.data || { defaults: [], personal: [] });
    } catch {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const list = tab === "defaults" ? data.defaults : data.personal;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      (r.title || "").toLowerCase().includes(q) ||
      (r.shortcut || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  const startCreate = () => {
    setEditing({ id: null, scope: tab === "defaults" ? "default" : "personal", title: "", shortcut: "", body_html: "" });
    setCreating(true);
  };

  const startEdit = (r) => { setEditing({ ...r }); setCreating(false); };

  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!editing.title.trim()) return toast.error("El título es obligatorio");
    try {
      if (creating) {
        await axios.post(`${API}/quick-replies`, {
          title: editing.title,
          shortcut: editing.shortcut,
          body_html: editing.body_html,
          scope: editing.scope,
        }, { withCredentials: true });
        toast.success("Plantilla creada");
      } else {
        await axios.put(`${API}/quick-replies/${editing.id}`, {
          title: editing.title,
          shortcut: editing.shortcut,
          body_html: editing.body_html,
        }, { withCredentials: true });
        toast.success("Plantilla actualizada");
      }
      close();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar plantilla "${r.title}"?`)) return;
    try {
      await axios.delete(`${API}/quick-replies/${r.id}`, { withCredentials: true });
      toast.success("Plantilla eliminada");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const canEdit = (r) => {
    if (r.scope === "default") return isAdmin;
    return isAdmin || r.owner_id === user?.id;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="quick-replies-page">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ModuleHeaderIcon icon={Zap} />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Respuestas rápidas y plantillas
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Plantillas para responder tickets más rápido. Las "Por defecto" están disponibles para todo el equipo;
                las "Personales" son solo tuyas.
              </p>
            </div>
          </div>
          <Button onClick={startCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="qr-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva plantilla
          </Button>
        </div>

        <div className="flex gap-2 border-b">
          <button
            onClick={() => setTab("defaults")}
            className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors flex items-center gap-2 ${tab === "defaults" ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="qr-tab-defaults"
          >
            <Globe className="w-4 h-4" /> Por defecto
            <Badge variant="outline" className="ml-1 text-[10px]">{data.defaults.length}</Badge>
          </button>
          <button
            onClick={() => setTab("personal")}
            className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors flex items-center gap-2 ${tab === "personal" ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="qr-tab-personal"
          >
            <UserIcon className="w-4 h-4" /> Personales
            <Badge variant="outline" className="ml-1 text-[10px]">{data.personal.length}</Badge>
          </button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por título o atajo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="qr-search"
          />
        </div>

        {tab === "defaults" && !isAdmin && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
            Estas plantillas son globales. Solo el administrador puede crearlas o editarlas — pero todos las pueden usar al responder un ticket.
          </div>
        )}

        {loading ? (
          <p className="text-slate-400 text-sm">Cargando...</p>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-400 text-sm">
            {search ? "Sin resultados para tu búsqueda" : "Aún no hay plantillas en esta sección"}
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((r) => (
              <Card key={r.id} className="hover:shadow-md transition-shadow" data-testid={`qr-card-${r.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                        {r.title}
                        {r.shortcut && <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{r.shortcut}</code>}
                      </CardTitle>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {r.scope === "default" ? "Por defecto" : `Personal · ${r.owner_name || ""}`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {canEdit(r) && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => startEdit(r)} className="h-8 w-8" title="Editar" data-testid={`qr-edit-${r.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(r)} className="h-8 w-8 text-red-500 hover:text-red-700" title="Eliminar" data-testid={`qr-delete-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div
                    className="prose prose-sm max-w-none text-xs text-slate-600 line-clamp-4 border-t pt-2"
                    dangerouslySetInnerHTML={{ __html: r.body_html || "<em>(sin contenido)</em>" }}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {creating ? "Nueva plantilla" : "Editar plantilla"}
              {editing && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {editing.scope === "default" ? "Por defecto" : "Personal"}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="qr-title">Título *</Label>
                  <Input
                    id="qr-title"
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="Ej. Acuse de recibo"
                    data-testid="qr-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="qr-shortcut">Atajo (opcional)</Label>
                  <Input
                    id="qr-shortcut"
                    value={editing.shortcut || ""}
                    onChange={(e) => setEditing({ ...editing, shortcut: e.target.value })}
                    placeholder="/saludo"
                    data-testid="qr-shortcut-input"
                  />
                </div>
              </div>
              <div>
                <Label>Contenido</Label>
                <RichTextEditor
                  value={editing.body_html}
                  onChange={(html) => setEditing({ ...editing, body_html: html })}
                  minHeight={260}
                />
              </div>
              {creating && isAdmin && (
                <div className="flex items-center gap-2 text-sm">
                  <input
                    id="qr-scope-default"
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600"
                    checked={editing.scope === "default"}
                    onChange={(e) => setEditing({ ...editing, scope: e.target.checked ? "default" : "personal" })}
                    data-testid="qr-scope-default"
                  />
                  <label htmlFor="qr-scope-default" className="cursor-pointer">
                    Hacer disponible para todo el equipo (por defecto)
                  </label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="qr-save-btn">
              {creating ? "Crear" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
